export interface User {
  id: string
  email: string
  displayName: string
  photoURL?: string
  createdAt: Date
  lastLoginAt: Date
  isActive: boolean
  // Avatar information
  avatarSeed?: string
  avatarStyle?: string
  customAvatar?: string

  // Avataaars customization
  avatarCustomization?: {
    skinColor?: string
    top?: string
    hairColor?: string
    clothing?: string
    eyes?: string
    mouth?: string
    accessories?: string
  }

  // Subscription
  subscriptionPlan?: 'free' | 'premium'
  subscriptionStatus?: 'active' | 'expired' | 'canceled'
}

export interface PetAvatarConfig {
  // Colores principales
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string; // Nuevo color terciario para detalles adicionales
  backgroundColor: string;

  // Colores específicos por parte
  faceColor: string;
  earColor: string;
  earInnerColor: string; // Color interior de las orejas
  eyeColor: string;
  muzzleColor: string;
  hairColor: string;
  browColor: string;
  patternColor: string;

  // Partes del avatar
  earVariant: number;
  eyeVariant: number;
  faceVariant: number;
  hairVariant: number;
  muzzleVariant: number;
  browVariant: number;
  patternVariant: number;

  // Opciones
  hasPattern: boolean;
  hasHair: boolean;
  hasBrows: boolean;

  // Características avanzadas
  outlineEnabled: boolean;
  outlineColor: string;
  outlineWidth: number;
  useGradientBackground: boolean;
  gradientStartColor: string;
  gradientEndColor: string;
  avatarScale: number; // Factor de escala (0.5 - 2.0)

  // Configuración de contornos por parte
  faceOutlineEnabled: boolean;
  faceOutlineColor: string;
  earOutlineEnabled: boolean;
  earOutlineColor: string;
  eyeOutlineEnabled: boolean;
  eyeOutlineColor: string;
  muzzleOutlineEnabled: boolean;
  muzzleOutlineColor: string;
  hairOutlineEnabled: boolean;
  hairOutlineColor: string;
  browOutlineEnabled: boolean;
  browOutlineColor: string;
  patternOutlineEnabled: boolean;
  patternOutlineColor: string;

  // Control de colores específicos
  useIndividualColors: boolean; // Si es false, usa primaryColor/secondaryColor para todo
}

export interface CareCase {
  id: string
  name: string // "Medicamentos de la Abuela", "Cuidado de Max (perro)", etc.
  description?: string
  profileType: 'human' | 'pet'
  profileInfo: {
    name: string
    age?: number
    gender?: 'male' | 'female' | 'other'
    weight?: number
    allergies?: string[]
    conditions?: string[]
    emergencyContact?: {
      name: string
      phone: string
      relationship: string
    }
    // Avatar y personalización
    avatarSeed?: string
    avatarStyle?: string
    customAvatar?: string
    avatarCustomization?: {
      skinColor?: string
      top?: string
      hairColor?: string
      clothing?: string
      eyes?: string
      mouth?: string
    }
    petAvatarConfig?: PetAvatarConfig
    // Preferencias que el admin puede preconfigurar
    preferences?: {
      language?: string
      timezone?: string
      notificationPreferences?: {
        email?: boolean
        push?: boolean
        sms?: boolean
      }
      medicationReminders?: boolean
      stockAlerts?: boolean
    }
    // Información adicional
    notes?: string
    medicalHistory?: string[]
    currentMedications?: string[]
  }
  adminId: string // Usuario que creó el caso
  profileUserId?: string // Usuario que reclamó el perfil (si es persona real)
  profileInviteCode?: string // Código específico para que la persona reclame su perfil
  createdAt: Date
  updatedAt: Date
  isActive: boolean
  inviteCode?: string // Código para invitar a otros usuarios
  settings: {
    allowGuestAccess: boolean
    requireApprovalForNewMembers: boolean
    notificationSettings: {
      adminGetsAllNotifications: boolean
      membersCanReceiveNotifications: boolean
    }
  }
}

export type UserRole =
  | 'master_admin'     // Administrador maestro (puede designar otros admins)
  | 'admin'            // Administrador (puede configurar otros usuarios)
  | 'caregiver'        // Cuidador (acceso completo a medicamentos, edita su perfil)
  | 'care_recipient'   // Persona bajo cuidado (puede ver su propio historial)

export interface UserPermissions {
  // Medicamentos
  canAddMedications: boolean
  canEditMedications: boolean
  canDeleteMedications: boolean
  canMarkAsTaken: boolean

  // Stock
  canAdjustStock: boolean
  canViewStock: boolean

  // Historial y reportes
  canViewHistory: boolean
  canViewReports: boolean
  canExportData: boolean

  // Usuarios y configuración
  canManageUsers: boolean
  canViewUsers: boolean
  canChangeSettings: boolean
  canDeleteCase: boolean
  canPromoteToAdmin: boolean // Solo master_admin puede promover a admin

  // Notificaciones
  canReceiveNotifications: boolean
  canConfigureNotifications: boolean
  canConfigureOthersNotifications: boolean // Solo admin y master_admin
}

export interface CaseMember {
  id: string
  userId: string
  caseId: string
  role: UserRole
  permissions: UserPermissions
  joinedAt: Date
  invitedBy: string
  isActive: boolean
  nickname?: string // "Hijo mayor", "Cuidadora nocturna", etc.
  notes?: string
}

export interface InviteRequest {
  id: string
  caseId: string
  invitedEmail: string
  invitedBy: string
  role: UserRole
  message?: string
  status: 'pending' | 'accepted' | 'rejected' | 'expired'
  createdAt: Date
  expiresAt: Date
}

// Helper function to get default permissions for a role
export const getDefaultPermissions = (role: UserRole): UserPermissions => {
  const basePermissions: UserPermissions = {
    canAddMedications: false,
    canEditMedications: false,
    canDeleteMedications: false,
    canMarkAsTaken: false,
    canAdjustStock: false,
    canViewStock: false,
    canViewHistory: false,
    canViewReports: false,
    canExportData: false,
    canManageUsers: false,
    canChangeSettings: false,
    canDeleteCase: false,
    canPromoteToAdmin: false,
    canReceiveNotifications: false,
    canConfigureNotifications: false,
    canConfigureOthersNotifications: false,
    canViewUsers: false,
  }

  switch (role) {
    case 'master_admin':
      return {
        canAddMedications: true,
        canEditMedications: true,
        canDeleteMedications: true,
        canMarkAsTaken: true,
        canAdjustStock: true,
        canViewStock: true,
        canViewHistory: true,
        canViewReports: true,
        canExportData: true,
        canManageUsers: true,
        canChangeSettings: true,
        canDeleteCase: true,
        canPromoteToAdmin: true,
        canReceiveNotifications: true,
        canConfigureNotifications: true,
        canConfigureOthersNotifications: true,
        canViewUsers: true,
      }

    case 'admin':
      return {
        canAddMedications: true,
        canEditMedications: true,
        canDeleteMedications: true,
        canMarkAsTaken: true,
        canAdjustStock: true,
        canViewStock: true,
        canViewHistory: true,
        canViewReports: true,
        canExportData: true,
        canManageUsers: true,
        canChangeSettings: true,
        canDeleteCase: true,
        canPromoteToAdmin: false,
        canReceiveNotifications: true,
        canConfigureNotifications: true,
        canConfigureOthersNotifications: true,
        canViewUsers: true,
      }

    case 'caregiver':
      return {
        ...basePermissions,
        canAddMedications: true,
        canEditMedications: true,
        canDeleteMedications: true,
        canMarkAsTaken: true,
        canAdjustStock: true,
        canViewStock: true,
        canViewHistory: true,
        canViewReports: true,
        canExportData: true,
        canReceiveNotifications: true,
        canConfigureNotifications: true,
        canViewUsers: true,
      }

    case 'care_recipient':
      return {
        ...basePermissions,
        canMarkAsTaken: true,
        canViewStock: true,
        canViewHistory: true,
        canViewReports: true,
        canReceiveNotifications: true,
        canConfigureNotifications: true,
        canViewUsers: true,
      }

    default:
      return basePermissions
  }
}

// Helper function to get role display info
export const getRoleInfo = (role: UserRole) => {
  const roleInfo = {
    master_admin: {
      name: 'Maestro',
      description: 'Administrador maestro - Control total y puede designar otros administradores',
      color: 'red',
      icon: '👑'
    },
    admin: {
      name: 'Administrador',
      description: 'Puede gestionar medicamentos y configurar otros usuarios',
      color: 'blue',
      icon: '⚡'
    },
    caregiver: {
      name: 'Cuidador',
      description: 'Puede gestionar medicamentos libremente y configurar su propio perfil',
      color: 'green',
      icon: ''
    },
    care_recipient: {
      name: 'Persona bajo cuidado',
      description: 'Persona bajo cuidado (puede ver su propio historial)',
      color: 'teal',
      icon: '👤'
    }
  }

  return roleInfo[role]
}

// Helper function to migrate old roles to new simplified system
export const migrateRole = (oldRole: string): UserRole => {
  const roleMigrationMap: Record<string, UserRole> = {
    // Admins stay as master_admin (first one) or admin
    'admin': 'master_admin', // By default, convert to master_admin

    // All other roles become caregivers
    'caregiver': 'caregiver',
    'stock_manager': 'caregiver',
    'editor': 'caregiver',
    'viewer': 'caregiver',
    'guest': 'caregiver',

    // Special case
    'care_recipient': 'care_recipient'
  }

  return roleMigrationMap[oldRole] || 'caregiver'
}

// Helper function to determine if a role should be downgraded in migration
export const shouldDowngradeAdmin = (oldRole: string, isFirstAdmin: boolean): UserRole => {
  if (oldRole === 'admin') {
    // First admin becomes master_admin, others become regular admin
    return isFirstAdmin ? 'master_admin' : 'admin'
  }
  return migrateRole(oldRole)
} 